// i18n.ts - Internationalization utilities
// Pattern from Aletheia/Box Data Sharing

import { safeGetItem, safeSetItem } from "./storage.js";

export const SUPPORTED_LANGUAGES = ["en", "nb", "nn"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const FALLBACK_LANGUAGE: SupportedLanguage = "en";

// Maps BCP 47 tags to supported languages
export const LANGUAGE_SELECTION_RULES: [RegExp, SupportedLanguage][] = [
  [/^en(?:-[a-z0-9]{1,8})*$/i, "en"],
  [/^(?:no|nb)(?:-[a-z0-9]{1,8})*$/i, "nb"],
  [/^nn(?:-[a-z0-9]{1,8})*$/i, "nn"],
];

export type Translations = Record<string, string>;
export type Dictionary = {
  language: SupportedLanguage;
  translations: Translations;
};

let currentDictionary: Dictionary | null = null;

/**
 * Select language from BCP 47 tag using rules
 */
export function selectLanguage(
  requestedLanguage: string,
): SupportedLanguage | null {
  for (const [regexp, language] of LANGUAGE_SELECTION_RULES) {
    if (regexp.test(requestedLanguage)) {
      return language;
    }
  }
  return null;
}

export const LANG_STORAGE_KEY = "argus-lang";

/**
 * Get preferred language from hints, URL param, or browser settings
 */
export function getPreferredLanguage(
  uiLocalesHint: string | null = null,
): SupportedLanguage {
  const stored = safeGetItem(LANG_STORAGE_KEY);
  if (stored && (SUPPORTED_LANGUAGES as readonly string[]).includes(stored)) {
    return stored as SupportedLanguage;
  }

  const urlHint = new URLSearchParams(window.location.search).get("ui_locales");
  const hints = uiLocalesHint ?? urlHint;

  const requestedLanguages: string[] = [
    ...(hints ? hints.split(" ") : []),
    ...window.navigator.languages,
  ];

  for (const requested of requestedLanguages) {
    const selected = selectLanguage(requested);
    if (selected) {
      return selected;
    }
  }

  return FALLBACK_LANGUAGE;
}

/**
 * Apply a language override: validate, persist, translate, and set html[lang]
 */
export function applyLanguage(
  lang: SupportedLanguage,
  dictionaries: Record<SupportedLanguage, Translations>,
): void {
  if (!(SUPPORTED_LANGUAGES as readonly string[]).includes(lang)) {
    return;
  }
  safeSetItem(LANG_STORAGE_KEY, lang);
  translate(dictionaries, lang);
}

/**
 * Translate all elements with data-i18n attribute
 */
export function translate(
  dictionaries: Record<SupportedLanguage, Translations>,
  uiLocalesHint: string | null = null,
  scope: HTMLElement | Document = document,
): Dictionary {
  const language = getPreferredLanguage(uiLocalesHint);
  const translations =
    dictionaries[language] ?? dictionaries[FALLBACK_LANGUAGE];

  const dictionary: Dictionary = { language, translations };
  currentDictionary = dictionary;

  document.documentElement.lang = language;

  const elements = scope.querySelectorAll<HTMLElement>("[data-i18n]");
  for (const element of elements) {
    const key = element.getAttribute("data-i18n")!;
    const value = translations[key];

    if (value) {
      element.textContent = value;
    }
  }

  // Handle aria-label translations
  const ariaElements = scope.querySelectorAll<HTMLElement>("[data-i18n-aria]");
  for (const element of ariaElements) {
    const key = element.getAttribute("data-i18n-aria")!;
    const value = translations[key];

    if (value) {
      element.setAttribute("aria-label", value);
    }
  }

  // Handle placeholder translations
  const placeholderElements = scope.querySelectorAll<HTMLElement>(
    "[data-i18n-placeholder]",
  );
  for (const element of placeholderElements) {
    const key = element.getAttribute("data-i18n-placeholder")!;
    const value = translations[key];
    if (value && element instanceof HTMLInputElement) {
      element.placeholder = value;
    }
  }

  // Handle href translations
  const hrefElements = scope.querySelectorAll<HTMLElement>("[data-i18n-href]");
  for (const element of hrefElements) {
    const key = element.getAttribute("data-i18n-href")!;
    const value = translations[key];
    if (value && element instanceof HTMLAnchorElement) {
      element.href = value;
    }
  }

  // Update page title if present
  const titleKey = "page_title";
  if (translations[titleKey]) {
    document.title = translations[titleKey];
  }

  return dictionary;
}

/**
 * Get a single translation by key (for dynamic content)
 */
export function t(
  key: string,
  replacements?: Record<string, string | number>,
): string {
  let value = currentDictionary?.translations[key] ?? `[${key}]`;

  if (replacements) {
    for (const [placeholder, replacement] of Object.entries(replacements)) {
      value = value.replace(`{${placeholder}}`, String(replacement));
    }
  }

  return value;
}

/**
 * Get current dictionary (for use in rendering functions)
 */
export function getCurrentDictionary(): Dictionary | null {
  return currentDictionary;
}
